import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { defaultTypeFor, findBasAccount } from '../bas';
import { starterAccountNumbers } from '../bas/starter';
import type { Account, AccountEvent, Company, Uuid } from '../types';
import { validateBasActivation, validateCustomAccount, type AccountRuleError, type CustomAccountInput } from './rules';

export class AccountRuleViolation extends Error {
  constructor(public readonly errors: AccountRuleError[]) {
    super(errors.map((e) => e.message).join('; '));
    this.name = 'AccountRuleViolation';
  }
}

async function existingNumbers(companyId: Uuid, db: BookkeepingDB): Promise<Set<number>> {
  const accounts = await db.accounts.where('companyId').equals(companyId).toArray();
  return new Set(accounts.map((a) => a.number));
}

function newEvent(partial: Omit<AccountEvent, 'id' | 'at'>): AccountEvent {
  return { id: crypto.randomUUID(), at: new Date().toISOString(), ...partial };
}

/** Called once when a company is created. Activates the entity's starter subset. */
export async function seedStarterAccounts(company: Company, fiscalYearId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
  await db.transaction('rw', db.accounts, db.accountEvents, async () => {
    const now = new Date().toISOString();
    for (const number of starterAccountNumbers(company.entityType)) {
      const bas = findBasAccount(number);
      if (!bas) throw new Error(`Starter account ${number} is missing from the BAS reference`);
      await db.accounts.add({
        id: crypto.randomUUID(),
        companyId: company.id,
        number: bas.number,
        name: bas.name,
        sruCode: bas.sruCode,
        type: bas.type,
        origin: 'starter',
        active: true,
        createdAt: now,
      });
      await db.accountEvents.add(
        newEvent({ companyId: company.id, accountNumber: number, kind: 'activated', fiscalYearId, details: { origin: 'starter' } }),
      );
    }
  });
}

/**
 * Activate a BAS account for a company. The caller (UI) must have obtained
 * explicit user confirmation before calling this.
 */
export async function activateBasAccount(
  input: { companyId: Uuid; number: number; fiscalYearId: Uuid },
  db: BookkeepingDB = defaultDb,
): Promise<Account> {
  return db.transaction('rw', db.accounts, db.accountEvents, async () => {
    const result = validateBasActivation(input.number, await existingNumbers(input.companyId, db));
    if (!result.ok) throw new AccountRuleViolation(result.errors);
    const bas = findBasAccount(input.number)!;
    const account: Account = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      number: bas.number,
      name: bas.name,
      sruCode: bas.sruCode,
      type: bas.type,
      origin: 'bas',
      active: true,
      createdAt: new Date().toISOString(),
    };
    await db.accounts.add(account);
    await db.accountEvents.add(
      newEvent({ companyId: input.companyId, accountNumber: bas.number, kind: 'activated', fiscalYearId: input.fiscalYearId, details: { origin: 'bas' } }),
    );
    return account;
  });
}

/** Create a custom account in a free BAS number. Requires an SRU code. */
export async function createCustomAccount(
  input: CustomAccountInput & { companyId: Uuid; fiscalYearId: Uuid },
  db: BookkeepingDB = defaultDb,
): Promise<Account> {
  return db.transaction('rw', db.accounts, db.accountEvents, async () => {
    const result = validateCustomAccount(input, await existingNumbers(input.companyId, db));
    if (!result.ok) throw new AccountRuleViolation(result.errors);
    const account: Account = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      number: input.number,
      name: input.name.trim(),
      sruCode: input.sruCode.trim(),
      type: defaultTypeFor(input.number),
      origin: 'custom',
      active: true,
      createdAt: new Date().toISOString(),
    };
    await db.accounts.add(account);
    await db.accountEvents.add(
      newEvent({
        companyId: input.companyId,
        accountNumber: input.number,
        kind: 'created',
        fiscalYearId: input.fiscalYearId,
        details: { name: account.name, sruCode: account.sruCode },
      }),
    );
    return account;
  });
}

async function isUsedInFiscalYear(companyId: Uuid, number: number, fiscalYearId: Uuid, db: BookkeepingDB): Promise<boolean> {
  const list = await db.verifikationer.where('fiscalYearId').equals(fiscalYearId).toArray();
  return list.some((v) => v.companyId === companyId && v.rows.some((r) => r.accountNumber === number));
}

/**
 * Change name and/or SRU code. Rejected if the account has been used in the
 * given fiscal year: meaning never changes within a year. The UI should offer
 * this only when preparing the next fiscal year.
 */
export async function renameAccount(
  input: { companyId: Uuid; number: number; fiscalYearId: Uuid; name?: string; sruCode?: string },
  db: BookkeepingDB = defaultDb,
): Promise<void> {
  await db.transaction('rw', db.accounts, db.accountEvents, db.verifikationer, async () => {
    const account = await db.accounts.where('[companyId+number]').equals([input.companyId, input.number]).first();
    if (!account) throw new Error('Account not found');
    if (await isUsedInFiscalYear(input.companyId, input.number, input.fiscalYearId, db)) {
      throw new AccountRuleViolation([
        { code: 'already_exists', message: 'Kontot har använts under räkenskapsåret och kan inte ändras förrän nästa år' },
      ]);
    }
    const changes: Partial<Account> = {};
    if (input.name !== undefined) changes.name = input.name.trim();
    if (input.sruCode !== undefined) changes.sruCode = input.sruCode.trim();
    await db.accounts.update(account.id, changes);
    await db.accountEvents.add(
      newEvent({
        companyId: input.companyId,
        accountNumber: input.number,
        kind: 'renamed',
        fiscalYearId: input.fiscalYearId,
        details: { fromName: account.name, toName: changes.name ?? account.name, fromSru: account.sruCode, toSru: changes.sruCode ?? account.sruCode },
      }),
    );
  });
}

export async function deactivateAccount(
  input: { companyId: Uuid; number: number; fiscalYearId: Uuid },
  db: BookkeepingDB = defaultDb,
): Promise<void> {
  await db.transaction('rw', db.accounts, db.accountEvents, db.verifikationer, async () => {
    const account = await db.accounts.where('[companyId+number]').equals([input.companyId, input.number]).first();
    if (!account) throw new Error('Account not found');
    if (await isUsedInFiscalYear(input.companyId, input.number, input.fiscalYearId, db)) {
      throw new AccountRuleViolation([{ code: 'already_exists', message: 'Kontot har använts under räkenskapsåret och kan inte avaktiveras' }]);
    }
    await db.accounts.update(account.id, { active: false });
    await db.accountEvents.add(
      newEvent({ companyId: input.companyId, accountNumber: input.number, kind: 'deactivated', fiscalYearId: input.fiscalYearId, details: {} }),
    );
  });
}

export async function listAccounts(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Account[]> {
  return db.accounts.where('companyId').equals(companyId).sortBy('number');
}
